import "@swisscom/sdx/dist/css/sdx.css";

import React, { useEffect, useState, useRef } from "react";
import { AuthFormLayout } from "./AuthorizationForm";
import Captcha, { useCaptcha } from "./captcha/captcha";
import {
  HTTP_429_TOO_MANY_REQUESTS,
  requestOtt,
  sendSignupRequest,
} from "./backendClientRequests";
import SdxInput from "../../helpers/SdxInputWrapper";

const MIN_LENGTH_OF_OTT = 8;

const REQUEST_SUCCESS = "REQUEST_SUCCESS";
const REQUEST_PENDING = "REQUEST_PENDING";
const REQUEST_FAILED = "REQUEST_FAILED";
const REQUEST_FAILED_TOO_MANY_REQUESTS = "REQUEST_FAILED_TOO_MANY_REQUESTS";

export default function SignupForm({ email, adminAddressList, onExit }) {
  const [requestOttStatus, setRequestOttStatus] = useState(undefined);
  const [signupRequestStatus, setSignupRequestStatus] = useState(undefined);
  const [requestOttErrorMessage, setRequestOttErrorMessage] =
    useState(undefined);

  const [ott, setOtt] = useState(undefined);

  var adminEmailRef = React.createRef();
  const [adminEmail, setAdminEmail] = useState(undefined);

  useEffect(() => {
    setAdminEmail(adminEmailRef.value?.[0]);
  }, [adminEmailRef.value?.[0]]);

  return (
    <div>
      <AuthFormLayout header={`Registration of ${email}`}>
        <div className="foo">
          <div
            className="card card--message-info card--icon card--border card--rounded card--clickable"
            aria-labelledby="Information"
            style={{ paddingBottom: "20px" }}
          >
            <i
              className="card__item-icon icon icon-046-information-circle icon--s3"
              aria-hidden="true"
            ></i>
            <div className="card__body">
              <div>
                {`The email address (${email}) is not currently registered as a delisting email address in our system.
  Delisting requests may only be submitted either by a domain administrator using one of the predefined administrator email addresses listed below, or by an email address that has already been registered specifically for delisting purposes.`}
              </div>

              <div style={{ textDecoration: "underline", marginTop: "20px" }}>
                Administrator addresses:
              </div>
              <div style={{ marginLeft: "10px" }}>
                <ul>
                  {adminAddressList.map((adr) => (
                    <li key={adr}>{adr}</li>
                  ))}
                </ul>
              </div>

              <div style={{ marginTop: "20px" }}>
                {`If required, a domain administrator may use the form on this page to register ${email} as a delisting email address.`}
              </div>
            </div>
          </div>

          <section style={{ paddingTop: "40px" }}>
            <sdx-select
              label="Select your admin address"
              placeholder="<empty>"
              ref={(adminEmail) => (adminEmailRef = adminEmail)}
              required
              disable={
                requestOttStatus === REQUEST_SUCCESS ||
                requestOttStatus === REQUEST_PENDING
              }
              disabled={
                requestOttStatus === REQUEST_SUCCESS ||
                requestOttStatus === REQUEST_PENDING
              }
            >
              {adminAddressList.map((adminEmail) => (
                <sdx-select-option
                  value={adminEmail}
                  key={adminEmail}
                  onClick={() => {
                    setAdminEmail(adminEmail);
                    setRequestOttErrorMessage(undefined);
                  }}
                >
                  {adminEmail}
                </sdx-select-option>
              ))}
            </sdx-select>

            {requestOttErrorMessage && (
              <div className="row margin-bottom-2">
                <div className="col" style={{ color: "red" }}>
                  {requestOttErrorMessage}
                </div>
              </div>
            )}

            <div
              style={{ display: "flex", alignItems: "flex-end", gap: "16px" }}
            >
              {requestOttStatus !== REQUEST_SUCCESS && (
                <RequestOttButton
                  hide={requestOttStatus === REQUEST_SUCCESS}
                  disable={
                    adminEmail == null || requestOttStatus === REQUEST_PENDING
                  }
                  adminEmail={adminEmail}
                  onClick={() => {
                    setRequestOttStatus(REQUEST_PENDING);
                    setRequestOttErrorMessage(undefined);
                  }}
                  onOttSent={() => {
                    setRequestOttStatus(REQUEST_SUCCESS);
                  }}
                  onFail={(tooManyRequests) => {
                    setRequestOttErrorMessage(
                      tooManyRequests
                        ? "Too many requests. Please wait a minute before trying again!"
                        : "Something went wrong.",
                    );
                    setRequestOttStatus(REQUEST_FAILED);
                  }}
                  onAbort={onExit}
                />
              )}
            </div>
          </section>

          <section
            style={{ paddingTop: "40px" }}
            hidden={requestOttStatus != REQUEST_SUCCESS}
          >
            <div className="row margin-bottom-2">
              <div className="col">
                A verification code was sent to{" "}
                <b>
                  <i>{adminEmail}</i>
                </b>
                . Copy the code into the field below and complete the
                registration of <i>{email}</i>.
              </div>
            </div>

            <div
              className="row margin-bottom-2"
              style={{ display: "flex", alignItems: "flex-end", gap: "40px" }}
            >
              <div
                className="col"
                style={{ flex: "1 1 auto", minWidth: 0, maxWidth: "400px" }}
              >
                <SdxInput
                  id="ott-input-field"
                  label={"Verification code"}
                  disabled={signupRequestStatus !== undefined}
                  required
                  type="text"
                  value={ott}
                  onChange={(val) => {
                    const ottTrim = val.trim();
                    setOtt(ottTrim);
                  }}
                />
              </div>

              <SignupButtons
                onExit={onExit}
                onSignupRequestResponse={(responseCode) => {
                  setSignupRequestStatus(responseCode);
                }}
                onRetry={() => {
                  setRequestOttStatus(undefined);
                  setSignupRequestStatus(undefined);
                  setRequestOttErrorMessage(undefined);
                  setOtt(undefined);
                }}
                email={email}
                adminEmail={adminEmail}
                ott={ott}
              />
            </div>
          </section>
          <div
            style={{
              marginTop: "100px",
            }}
          ></div>
        </div>
      </AuthFormLayout>
      <SignupCompletionNotification
        onClose={() => {
          if (signupRequestStatus == REQUEST_SUCCESS) {
            onExit();
          } else {
            setSignupRequestStatus(null);
          }
        }}
        email={email}
        requestStatus={signupRequestStatus}
      />
    </div>
  );
}

function SignupButtons({
  onExit,
  onSignupRequestResponse,
  onRetry,
  email,
  adminEmail,
  ott,
}) {
  const [showAsPending, setShowAsPending] = useState(false);
  const [showRetryButton, setShowRetryButton] = useState(false);

  return (
    <sdx-button-group layout="fixed">
      <sdx-button
        id="signupBackButtonAbort"
        theme={"secondary"}
        label={"Cancel"}
        onClick={() => {
          onExit();
        }}
      ></sdx-button>
      {!showRetryButton ? (
        <sdx-button
          id="signupBackButton2"
          label={"Complete registration"}
          onClick={() => {
            setShowAsPending(true);
            sendSignupRequest(email, adminEmail, ott)
              .then(() => {
                onSignupRequestResponse(REQUEST_SUCCESS);
              })
              .catch(() => {
                onSignupRequestResponse(REQUEST_FAILED);
                setShowRetryButton(true);
              })
              .finally(() => {
                setShowAsPending(false);
              });
          }}
          loading={showAsPending}
          disabled={ott == null || ott.length < MIN_LENGTH_OF_OTT}
        ></sdx-button>
      ) : (
        <sdx-button
          id="retryButton"
          label={"Restart"}
          onClick={() => {
            setShowRetryButton(false);
            onRetry();
          }}
        ></sdx-button>
      )}
    </sdx-button-group>
  );
}

function RequestOttButton({
  hide,
  disable,
  adminEmail,
  onClick,
  onOttSent,
  onFail,
  onAbort,
}) {
  const [showAsPending, setShowAsPending] = useState(false);
  const { validateCaptchaCompletion, getCaptchaResponse, showCaptchaError } =
    useCaptcha();

  return (
    !hide && (
      <div style={{ paddingTop: "20px" }}>
        <Captcha showErrorMessage={showCaptchaError} />

        <sdx-button-group layout="fixed">
          <sdx-button
            id="signupBackButtonAbort"
            theme={"secondary"}
            label={"Cancel"}
            onClick={() => {
              onAbort();
            }}
          ></sdx-button>
          <sdx-button
            id="requestLoginOttButton"
            label={"Next"}
            onClick={() => {
              if (validateCaptchaCompletion()) {
                onClick();
                setShowAsPending(true);
                requestOtt(adminEmail, getCaptchaResponse())
                  .then((response) => {
                    if (response.sentOtt) {
                      onOttSent();
                      return;
                    }
                    throw new Error("Unexpected response during OTT request.");
                  })
                  .catch((e) => {
                    const tooManyRequests =
                      e.status == HTTP_429_TOO_MANY_REQUESTS;
                    onFail(tooManyRequests);
                  })
                  .finally(() => {
                    setShowAsPending(false);
                  });
              }
            }}
            loading={showAsPending}
            disabled={disable}
          ></sdx-button>
        </sdx-button-group>
      </div>
    )
  );
}

export function SignupCompletionNotification({
  onClose,
  requestStatus,
  email,
}) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!dialogRef.current) return;

    customElements.whenDefined("sdx-dialog").then(() => {
      const open =
        requestStatus === REQUEST_SUCCESS || requestStatus === REQUEST_FAILED;

      if (open) {
        dialogRef.current.open();
      } else {
        dialogRef.current.close();
      }
    });
  }, [requestStatus]);

  return (
    <sdx-dialog
      ref={dialogRef}
      notification-type={
        requestStatus === REQUEST_SUCCESS ? "confirmation" : "warning"
      }
      label={
        requestStatus === REQUEST_SUCCESS ? "Registration complete!" : "Failed"
      }
      type="modal"
    >
      <sdx-dialog-content class="text-align-center">
        <p
          className="text-align-center margin-bottom-4"
          style={{ maxWidth: "600px" }}
        >
          {requestStatus === REQUEST_SUCCESS
            ? `The email address ${email} can now be used to send delisting requests.`
            : requestStatus === REQUEST_FAILED_TOO_MANY_REQUESTS
              ? `Too many requests in a short amount of time. Please wait a minute before trying again!`
              : `Registration of ${email} failed. Please restart the process to request a new verification code!
              `}
        </p>
        <sdx-button label="Close" onClick={onClose} />
      </sdx-dialog-content>
    </sdx-dialog>
  );
}
